/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { BACKEND_URL, env } from "../../../config/config";
import axios from "axios";
import { currencyFormatter } from "../../../helpers/currencyHelper";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

import { Button, Select, DatePicker, Space, message } from "antd";
import Modal from "../../Modal/Modal";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { ExportPdf, ExportCsv } from "@material-table/exporters";
import { ClearOutlined } from "@ant-design/icons";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { OpenModalJobTimeLine } from "../OpenModalJobTimeLine/OpenModalJobTimeLine";
import "./ProposalList.css";
import { OpenModalAccount } from "../../Account";
import {
  URL_INTERACTIVE_CALCULATOR,
  URL_INTERACTIVE_CALCULATOR_TEST,
} from "../../../config/constants";
import { useAuth } from "../../../hooks";
import { OpenDocumentList } from "../../Dashboard";

const { RangePicker } = DatePicker;

export default function PropsalList({
  proposal,
  title,
  pageSize,
  reload,
  setReload,
  handleRefreshChange,
  dataChart,
}) {
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState(null);
  dayjs.extend(quarterOfYear);
  // filtros
  const [dateFilter, setDateFilter] = useState(undefined); // etiqueta del quick filter
  const [statusFilter, setStatusFilter] = useState(undefined); // id o label mostrado
  const [dateRange, setDateRange] = useState(null); // [dayjs|null, dayjs|null]

  const { user } = useAuth();

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const theme = createTheme(
    { palette: { primary: { main: "#1976d2" } } },
    esES,
  );

  // ===================== Helpers =====================
  const fmtDate = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
  const fmtAmount = (n) => currencyFormatter(Number(n ?? 0));
  const getCompanyName = (row) =>
    (row?.account?.accountName || row?.lead?.companyName || "").toString();

  const getTrsApproved = (row) =>
    row?.status?._id === "WON" ||
    row?.status?._id === "WONADD" ||
    row?.status?._id === "APPRBYCUSTOMER"
      ? (row?.numberTRs ?? 0)
      : 0;

  const getStatusId = (row) => {
    const id =
      row?.status?._id ??
      row?.status?.id ??
      (typeof row?.status === "string" ? row.status : undefined);
    return (id || "").toString().trim().toUpperCase();
  };

  const getAmountNum = (row) => {
    const n = Number(row?.amount);
    return Number.isFinite(n) ? n : 0;
  };

  const canOpenInteractiveReport = (row) => {
    const approved = !!row?.approvedByCustomer;
    const statusId = getStatusId(row);
    const amountNum = getAmountNum(row);
    return !approved && statusId === "PROPOSAL" && amountNum > 0;
  };

  const renderStage = (rowData) => {
    const statusId = getStatusId(rowData);
    const isApproved =
      statusId === "WON" ||
      statusId === "WONADD" ||
      statusId === "APPRBYCUSTOMER";

    return (
      <span
        className={`table-status-pill ${
          isApproved
            ? "table-status-pill--success"
            : "table-status-pill--warning"
        }`}
      >
        {isApproved ? (
          <CheckCircleOutlineIcon className="table-status-pill__icon" />
        ) : (
          <AccessTimeOutlinedIcon className="table-status-pill__icon" />
        )}
        {rowData?.status?.displayName || rowData?.status?.description || "-"}
      </span>
    );
  };

  // ===================== Columnas (UI) =====================
  const proposalscolumns = [
    {
      title: "Company",
      field: "account.accountName",
      render: (rowData) => (
        <span
          onClick={() => onView(rowData?.account || rowData?.lead)}
          className="rowClick table-entity"
        >
          <span className="table-entity__icon">
            <BusinessOutlinedIcon />
          </span>
          <span className="table-entity__text">{getCompanyName(rowData)}</span>
        </span>
      ),
    },
    { title: "Proposal number", field: "proposalNo" },
    { title: "Proposal name", field: "opportunityName" },
    { title: "TRs Recom.", field: "numberTRs" },
    {
      title: "TRs Appr.",
      render: (rowData) => getTrsApproved(rowData),
    },
    {
      title: "Amount",
      field: "amount",
      render: (rowData) => <span>{fmtAmount(rowData?.amount)}</span>,
    },
    {
      title: "Stage",
      field: "status.displayName",
      render: renderStage,
    },
    { title: "PARC", field: "PARCAgreement.name" },
    {
      title: "Requested Date",
      field: "requestedDate",
      render: (rowData) => <span>{fmtDate(rowData?.requestedDate)}</span>,
    },
    {
      title: "Valid Until",
      field: "validUntil",
      render: (rowData) => <span>{fmtDate(rowData?.validUntil)}</span>,
    },
  ];

  // ===================== Acciones / Modales =====================
  const onView = (data) => {
    setIsVisibleModal(true);
    setModalTitle("Account information");
    setModalContent(<OpenModalAccount data={data} />);
  };

  const showProposalDocumentsList = async (data) => {
    const url = `${BACKEND_URL}/documentmasters/getByOpportunityId/${data._id}`;
    const { data: docs } = await axios.get(url).catch((err) => {
      console.log(err);
      return { data: [] };
    });

    const dataElement = Array.isArray(docs)
      ? docs.map((document) => ({
          documentName: document?.documentName,
          documentType: document?.documentType?.name,
          uploadedDate: fmtDate(document?.created),
          documentPath: document?.documentPath,
        }))
      : [];

    setIsVisibleModal(true);
    setModalTitle("Proposal Documents");
    setModalContent(
      <div style={{ width: "100%" }}>
        {dataElement.length ? (
          <OpenDocumentList data={dataElement} title="" />
        ) : (
          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <span style={{ fontWeight: "bold" }}>No documents</span>
          </div>
        )}
      </div>,
    );
  };

  const jobTimeLineModal = (data) => {
    setIsVisibleModal(true);
    setModalTitle(`Proposal job timeline # ${data.proposalNo}`);
    setModalContent(<OpenModalJobTimeLine proposal={data} />);
  };

  const openInteractiveReport = async (rowData) => {
    const base =
      env === "testing"
        ? URL_INTERACTIVE_CALCULATOR_TEST
        : URL_INTERACTIVE_CALCULATOR;
    const url = `${base}/${rowData.proposalNo}?portal=true&userID=${user._id}&userEmail=${user.email.main}`;
    window.open(url, "_blank");
  };

  // ===================== Filtros =====================
  const dateOptions = useMemo(() => {
    const map = new Map();
    map.set("THISMONTH", "This Month");
    map.set("LASTMONTH", "Last Month");
    map.set("THISQUARTER", "This Quarter");
    map.set("LASTQUARTER", "Last Quarter");
    map.set("THISYEAR", "This Year");
    map.set("LASTYEAR", "Last Year");
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [proposal]);

  const statusOptions = useMemo(() => {
    const map = new Map();
    (proposal || []).forEach((p) => {
      const value =
        p?.status?._id ||
        p?.status?.displayName ||
        p?.status?.description ||
        "UNKNOWN";
      const label =
        p?.status?.displayName ||
        p?.status?.description ||
        p?.status?._id ||
        "Unknown";
      if (value) map.set(String(value), String(label));
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [proposal]);

  // Quick filter -> guarda SIEMPRE dayjs()
  const handleDateFilter = (val) => {
    let from = null,
      to = null;

    switch (val) {
      case "THISMONTH":
        from = dayjs().startOf("month");
        to = dayjs().endOf("month");
        break;
      case "LASTMONTH":
        from = dayjs().subtract(1, "month").startOf("month");
        to = dayjs().subtract(1, "month").endOf("month");
        break;
      case "THISQUARTER":
        from = dayjs().startOf("quarter");
        to = dayjs().endOf("quarter");
        break;
      case "LASTQUARTER":
        from = dayjs().subtract(1, "quarter").startOf("quarter");
        to = dayjs().subtract(1, "quarter").endOf("quarter");
        break;
      case "THISYEAR":
        from = dayjs().startOf("year");
        to = dayjs().endOf("year");
        break;
      case "LASTYEAR":
        from = dayjs().subtract(1, "year").startOf("year");
        to = dayjs().subtract(1, "year").endOf("year");
        break;
      default:
        setDateRange(null);
        setDateFilter(undefined);
        return;
    }

    setDateRange([from, to]);
    setDateFilter(val);
  };

  // Manual (RangePicker v5 usa dayjs) -> guardamos dayjs
  const handleDateRangeChange = (vals) => {
    if (!vals || vals.length === 0) {
      setDateRange(null);
      setDateFilter(undefined);
      return;
    }
    const [from, to] = vals;
    setDateRange([from ? dayjs(from) : null, to ? dayjs(to) : null]);
    setDateFilter(undefined); // si el usuario elige manual, deselecciona quick
  };

  // Filtrado local por status + requestedDate
  const filteredData = useMemo(() => {
    const [from, to] = dateRange || [null, null];
    return (proposal || []).filter((row) => {
      if (statusFilter) {
        const current =
          row?.status?._id ||
          row?.status?.displayName ||
          row?.status?.description;
        if (String(current) !== String(statusFilter)) return false;
      }
      if (from || to) {
        const req = row?.requestedDate ? dayjs(row.requestedDate) : null;
        if (!req || !req.isValid()) return false;
        if (from && req.isBefore(from.startOf("day"))) return false;
        if (to && req.isAfter(to.endOf("day"))) return false;
      }
      return true;
    });
  }, [proposal, statusFilter, dateRange]);

  const clearFilters = () => {
    setStatusFilter(undefined);
    setDateFilter(undefined);
    setDateRange(null);
  };

  // ===================== Export (todas las filas filtradas) =====================
  const exportColumns = [
    { title: "Company", field: "company" },
    { title: "Proposal number", field: "proposalNo" },
    { title: "Proposal name", field: "opportunityName" },
    { title: "TRs Recom.", field: "trsRecom" },
    { title: "TRs Appr.", field: "trsAppr" },
    { title: "Amount", field: "amount" },
    { title: "Region", field: "region" },
    { title: "PARC", field: "parc" },
    { title: "Requested Date", field: "requestedDate" },
    { title: "Valid Until", field: "validUntil" },
  ];

  const buildExportRows = (rows) =>
    (rows || []).map((r) => ({
      company: getCompanyName(r),
      proposalNo: r?.proposalNo ?? "",
      opportunityName: r?.opportunityName ?? "",
      trsRecom: r?.numberTRs ?? 0,
      trsAppr: getTrsApproved(r),
      amount: fmtAmount(r?.amount),
      region: r?.region || "-",
      parc: r?.PARCAgreement?.name || "-",
      requestedDate: fmtDate(r?.requestedDate),
      validUntil: fmtDate(r?.validUntil),
    }));

  // ===================== UI =====================
  return (
    <>
      <div className="listContainer">
        <ThemeProvider theme={theme}>
          <MaterialTable
            columns={proposalscolumns}
            className="genericTable"
            components={{
              Toolbar: (toolbarProps) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <MTableToolbar {...toolbarProps} />
                  </div>
                  <Space wrap align="center">
                    <Select
                      allowClear
                      placeholder="Quick Filter"
                      className="filter-select"
                      value={dateFilter}
                      onChange={handleDateFilter}
                      options={dateOptions}
                    />
                    <Select
                      allowClear
                      placeholder="Filter by Status"
                      className="filter-select"
                      value={statusFilter}
                      onChange={(v) => setStatusFilter(v)}
                      options={statusOptions}
                    />
                    <RangePicker
                      value={dateRange || undefined} // dayjs[] | undefined
                      onChange={handleDateRangeChange}
                      className="filter-range-picker"
                      format="MM/DD/YYYY"
                      placeholder={["From", "To"]}
                      allowClear
                    />
                    <Button
                      className="filter-clear-btn"
                      icon={<ClearOutlined />}
                      onClick={clearFilters}
                    >
                      Clear
                    </Button>
                  </Space>
                </div>
              ),
            }}
            data={filteredData}
            actions={
              dataChart
                ? []
                : [
                    {
                      icon: () => (
                        <LocalShippingOutlinedIcon className="table-action-icon table-action-icon--warning" />
                      ),
                      tooltip: "Job timeline",
                      onClick: (_e, rd) => {
                        if (
                          !Array.isArray(rd?.jobTimeline) ||
                          rd.jobTimeline.length === 0
                        ) {
                          message.info("This proposal has no job timeline.");
                          return;
                        }
                        jobTimeLineModal(rd);
                      },
                    },
                    {
                      icon: () => (
                        <ArticleOutlinedIcon className="table-action-icon table-action-icon--view" />
                      ),
                      tooltip: "Open Interactive Report",
                      onClick: (_e, rd) => {
                        if (!canOpenInteractiveReport(rd)) {
                          const statusId = getStatusId(rd) || "UNKNOWN";
                          const amountNum = getAmountNum(rd);
                          const reason = rd?.approvedByCustomer
                            ? "Already approved by customer."
                            : statusId !== "PROPOSAL"
                              ? `Status is ${statusId}, must be PROPOSAL.`
                              : amountNum <= 0
                                ? "Amount must be greater than 0."
                                : "Not eligible.";
                          message.warning(
                            `Cannot open Interactive Report: ${reason}`,
                          );
                          return;
                        }
                        openInteractiveReport(rd);
                      },
                    },
                    {
                      icon: () => (
                        <FileOpenOutlinedIcon className="table-action-icon table-action-icon--documents" />
                      ),
                      tooltip: "Open Documents",
                      onClick: (_e, rd) => showProposalDocumentsList(rd),
                    },
                  ]
            }
            options={{
              actionsColumnIndex: -1,
              pageSize: pageSize || 20,
              filtering: false,
              headerStyle: {
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor: "white",
              },
              maxBodyHeight: `${windowSize.height - 250}px`,
              minBodyHeight: `${windowSize.height - 250}px`,
              exportAllData: true,
              exportMenu: [
                {
                  label: "Export to PDF",
                  exportFunc: () => {
                    const rows = buildExportRows(filteredData);
                    ExportPdf(
                      exportColumns,
                      rows,
                      "Proposal Report - " + new Date().toISOString(),
                    );
                  },
                },
                {
                  label: "Export to CSV",
                  exportFunc: () => {
                    const rows = buildExportRows(filteredData);
                    ExportCsv(
                      exportColumns,
                      rows,
                      "Proposal Report - " + new Date().toISOString(),
                    );
                  },
                },
              ],
            }}
            style={{ width: "100%" }}
            title={title}
          />
        </ThemeProvider>
      </div>

      <Modal
        title={modalTitle}
        open={isVisibleModal}
        setIsVisible={setIsVisibleModal}
        className={
          modalTitle === "Approve this proposal"
            ? null
            : "openModalDocumentsList"
        }
      >
        {modalContent}
      </Modal>
    </>
  );
}
